import type {TFunction} from 'i18next';
import {OFFER_CATEGORIES, UNIT_PRICE_TYPES} from '@chekin/core';
import type {ListDeal, Offer} from '@chekin/core';
import {formatDisplayDate} from '../guestForm/helpers';

type OfferCategoryInput = Partial<Pick<Offer, 'category'>> | null | undefined;

const TIME_BASED_CATEGORIES = new Set<string>([
  OFFER_CATEGORIES.checkInCheckOut,
  OFFER_CATEGORIES.checkIn,
  OFFER_CATEGORIES.checkOut,
]);

const isTimeBasedOffer = (offer?: OfferCategoryInput) =>
  Boolean(offer?.category && TIME_BASED_CATEGORIES.has(offer.category));

const isLuggageServiceOffer = (category?: OFFER_CATEGORIES | string): boolean =>
  category === OFFER_CATEGORIES.luggageServices;

function parseTimeFromName(name: string): string | null {
  const match = name.match(/\b(\d{1,2}):(\d{2})\b/);
  if (!match) return null;
  const hh = match[1].padStart(2, '0');
  const mm = match[2];
  if (parseInt(hh, 10) > 23 || parseInt(mm, 10) > 59) return null;
  return `${hh}:${mm}`;
}

const SELECTION_DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

type DealSelection = Pick<
  ListDeal,
  'requested_for' | 'number_of_people' | 'requested_items' | 'radical_storage_booking'
> & {offer: Pick<Offer, 'category'>};

type DealTitleSource = {offer?: Pick<Offer, 'title' | 'category'>};

type DealTitle = {
  title: string;
  variantName?: string;
  category?: string;
};

function resolveDealTitle(deal: DealTitleSource, currentTitle: string): DealTitle {
  const offerTitle = deal.offer?.title;
  const isVariantName = Boolean(offerTitle && offerTitle !== currentTitle);

  return {
    title: isVariantName && offerTitle ? offerTitle : currentTitle,
    variantName: isVariantName ? currentTitle : undefined,
    category: deal.offer?.category,
  };
}

function indexDealsById(deals?: ListDeal[]): Map<string, ListDeal> {
  return new Map(deals?.map(deal => [deal.id, deal]));
}

const PET_CATEGORIES = new Set<string>([
  OFFER_CATEGORIES.petFee,
  OFFER_CATEGORIES.petFriendly,
]);

function describeQuantity(
  quantity: number,
  category: string | undefined,
  unitType: UNIT_PRICE_TYPES | undefined,
  t: TFunction,
): string | null {
  if (quantity < 1) return null;
  if (PET_CATEGORIES.has(category ?? '')) {
    return t('deal_meta_pets', {count: quantity});
  }
  if (unitType === UNIT_PRICE_TYPES.person) {
    return t('deal_meta_quantity', {count: quantity});
  }
  return quantity > 1 ? t('times_count', {value: quantity}) : null;
}

function describeLuggageSelection(
  deal: DealSelection,
  t: TFunction,
  language?: string,
): string | null {
  const booking = deal.radical_storage_booking;
  const [item] = deal.requested_items ?? [];
  const bags = booking?.bags || item?.quantity || deal.number_of_people || 0;

  const parts: string[] = [];
  if (bags > 0) parts.push(t('luggage_form.bags_count', {count: bags}));

  const day = formatDisplayDate(booking?.drop_off ?? deal.requested_for, language, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  if (day) parts.push(day);

  const window = [booking?.drop_off, booking?.pick_up]
    .map(value => (value ? value.slice(11, 16) : null))
    .filter(Boolean);
  if (window.length === 2) parts.push(window.join(' → '));

  return parts.length > 0 ? parts.join(' · ') : null;
}

function describeDealSelection(
  deal: DealSelection,
  t: TFunction,
  language?: string,
  unitType?: UNIT_PRICE_TYPES,
): string | null {
  const category = deal.offer?.category;
  const [item] = deal.requested_items ?? [];
  const itemTime = item?.name ? parseTimeFromName(item.name) : null;
  const parts: string[] = [];

  if (isLuggageServiceOffer(category)) {
    return describeLuggageSelection(deal, t, language) ?? null;
  }

  if (isTimeBasedOffer({category})) {
    if (itemTime) {
      parts.push(
        category === OFFER_CATEGORIES.checkOut
          ? t('deal_meta_until_time', {time: itemTime})
          : t('deal_meta_from_time', {time: itemTime}),
      );
    }
  } else {
    const when = formatDisplayDate(
      deal.requested_for,
      language,
      SELECTION_DATE_TIME_OPTIONS,
    );
    if (when) {
      parts.push(when);
    }
  }

  const quantity = describeQuantity(
    Math.max(deal.number_of_people ?? 0, item?.quantity ?? 0),
    category,
    unitType,
    t,
  );
  if (quantity) {
    parts.push(quantity);
  }

  return parts.length > 0 ? parts.join(' · ') : null;
}

export {
  describeDealSelection,
  indexDealsById,
  isLuggageServiceOffer,
  isTimeBasedOffer,
  parseTimeFromName,
  resolveDealTitle,
};
export type {DealTitle};
